import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { fetchProductFromServer, fetchCartonLinesByLicensePlate, getTotalQuantityReceivedForSkuAcrossDocs } from '../helpers';
import axios from 'axios';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { z } from 'zod';

/**
 * Helper function to set toast text based on entry mode (manual or scan)
 */
const getToastForEntryMode = (entryMode, identifier) => {
    const isManualEntry = entryMode === 'manual-entry';

    return {
        type: 'success',
        message:
            isManualEntry === true
                ? PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.SUCCESSFULLY_RECEIVED_PRODUCT + identifier
                : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.SUCCESSFULLY_SCANNED_PRODUCT + identifier,
    };
};

/**
 * Helper function for efficient array updates with multiple items
 */
const updateOrAddItems = ({ prevArray, newItems, getKey, updateItem, createItem, onMatch, onAdd }) => {
    const prevLength = prevArray.length;
    const newItemsLength = newItems.length;

    // Build lookup map
    const itemMap = new Map();
    for (let i = 0; i < prevLength; i++) {
        const item = prevArray[i];
        const key = getKey(item);
        itemMap.set(key, { item, index: i });
    }

    // Track updates and additions
    const updates = new Map();
    const additions = [];

    // Process new items
    for (let i = 0; i < newItemsLength; i++) {
        const newItem = newItems[i];
        const key = getKey(newItem);
        const existing = itemMap.get(key);

        if (existing) {
            const { item, index } = existing;
            const updatedItem = updateItem(item, newItem);
            updates.set(index, updatedItem);
            itemMap.set(key, { item: updatedItem, index });

            if (onMatch) onMatch(updatedItem, index);
        } else {
            const itemToAdd = createItem ? createItem(newItem) : newItem;

            additions.push(itemToAdd);

            if (onAdd) {
                onAdd(itemToAdd, additions.length - 1);
            }
        }
    }

    // No changes
    if (updates.size === 0 && additions.length === 0) {
        return prevArray;
    }

    // Build result array
    const resultLength = prevLength + additions.length;
    const result = new Array(resultLength);

    for (let i = 0; i < additions.length; i++) {
        result[i] = additions[i];
    }

    // Copy with updates
    for (let i = 0; i < prevLength; i++) {
        result[additions.length + i] = updates.has(i) ? updates.get(i) : prevArray[i];
    }

    return result;
};

/**
 * Simplified helper for single item update/add
 */
const updateOrAddSingleItem = ({ prevArray, newItem, matcher, updateItem, createItem, onMatch, onAdd }) => {
    const prevLength = prevArray.length;
    let matchIndex = -1;

    // Find matching item
    for (let i = 0; i < prevLength; i++) {
        if (matcher(prevArray[i], newItem)) {
            matchIndex = i;
            break;
        }
    }

    if (matchIndex !== -1) {
        // Update existing
        const result = new Array(prevLength);
        for (let i = 0; i < prevLength; i++) {
            if (i === matchIndex) {
                result[i] = updateItem(prevArray[i], newItem);
                if (onMatch) onMatch(result[i], i);
            } else {
                result[i] = prevArray[i];
            }
        }
        return result;
    } else {
        // Add new
        const result = new Array(prevLength + 1);
        result[0] = createItem(newItem);

        for (let i = 0; i < prevLength; i++) {
            result[i + 1] = prevArray[i];
        }
        if (onAdd) {
            onAdd(result[0], 0);
        }

        return result;
    }
};

/**
 * Get expected quantity for a SKU from purchase order
 * Returns the quantity if found, null if inputs missing, undefined if not found
 */
const getQuantityExpectedForSku = (sku, purchaseOrder) => {
    // Guard clause: exit early if required data is missing
    if (!sku || !purchaseOrder?.purchaseOrderLines) return null;

    // Find the purchase order line that matches this SKU
    const line = purchaseOrder.purchaseOrderLines.find((l) => l.sku === sku);

    // Return quantity as number if line exists, undefined if not found
    return line ? Number(line.quantity) : undefined;
};

/**
 * Hook for managing async button loading states
 * Wraps any async function to provide loading state management
 */
export const useAsyncButton = (buttonTypeClick) => {
    const [loading, setLoading] = useState(false);

    // Wrap the button click handler with loading state management
    const handleClick = useCallback(async () => {
        setLoading(true);
        try {
            await buttonTypeClick();
        } finally {
            // Always reset loading state, even if the operation fails
            setLoading(false);
        }
    }, [buttonTypeClick]);

    return [loading, handleClick];
};

/**
 * Hook for fetching and managing purchase order line items
 * Handles API calls, data enrichment, and error states
 */
export const usePurchaseOrderLineItems = (poId, rdId, setScannedVariants, purchaseOrder) => {
    // State for storing raw line items from server
    const [lineItems, setLineItems] = useState([]);
    // Loading state for the fetch operation
    const [loading, setLoading] = useState(true);

    const { addToast } = useToast();

    useEffect(() => {
        const fetchLineItems = async () => {
            setLoading(true);

            try {
                // Make API request to fetch line items for this receiving document
                const response = await axios.post(
                    `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}/receiving-document/${rdId}`,
                    { action: 'fetch-lines' },
                    { headers: { 'Content-Type': 'application/json' } }
                );

                // Extract line items from response
                const fetchedLines = response.data?.data?.enrichedLineItems;

                // Check if this receiving document exists in the purchase order
                // This prevents syncing data for unsaved documents
                let docExists = false;
                if (purchaseOrder?.receivingDocuments) {
                    for (let i = 0; i < purchaseOrder.receivingDocuments.length; i++) {
                        if (String(purchaseOrder.receivingDocuments[i].id) === String(rdId)) {
                            docExists = true;
                            break;
                        }
                    }
                }

                // Only process data if we have valid line items and the document exists
                if (fetchedLines && purchaseOrder && docExists) {
                    // Enrich each line item with calculated quantities
                    const enrichedLines = [];
                    for (let i = 0; i < fetchedLines.length; i++) {
                        const item = fetchedLines[i];
                        enrichedLines.push({
                            ...item,
                            // Keep original expected quantity
                            quantityExpected: item.quantityExpected,
                            // Store quantity received in this document
                            qtyReceived: item.quantityReceived,
                            // Calculate total quantity received across all documents
                            quantityReceived: getTotalQuantityReceivedForSkuAcrossDocs(item.sku, purchaseOrder),
                        });
                    }

                    // Update state with enriched data
                    setScannedVariants(enrichedLines);
                    setLineItems(fetchedLines);
                }
            } catch (err) {
                // Set error message if fetch fails
                addToast({
                    type: 'error',
                    message: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.FAILED_TO_FETCH_LINE_ITEMS,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchLineItems();
    }, [poId, rdId, setScannedVariants, purchaseOrder]);

    return { lineItems, loading };
};

/**
 * Filter function for table items
 * Checks if an item matches all active filters
 */
const itemMatchesFilters = (item, filters) => {
    const { query, product, vendorStyle, sku, barcode, qtyExpected, qtyReceivedToDate, qtyReceived } = filters;

    // Check general query filter first (searches multiple fields)
    if (query) {
        const queryLower = query.toLowerCase();
        let matchesQuery = false;

        // Check each searchable field
        if (item.variant?.product?.title?.toLowerCase().includes(queryLower)) matchesQuery = true;
        if (item.variant?.title?.toLowerCase().includes(queryLower)) matchesQuery = true;
        if (item.variant?.sku?.toLowerCase().includes(queryLower)) matchesQuery = true;
        if (item.barcode?.toLowerCase().includes(queryLower)) matchesQuery = true;

        // If query doesn't match any field, exclude this item
        if (!matchesQuery) return false;
    }

    // Check individual field filters
    if (product && !item.variant?.product?.title?.toLowerCase().includes(product.toLowerCase())) {
        return false;
    }
    if (vendorStyle && !item.variant?.title?.toLowerCase().includes(vendorStyle.toLowerCase())) {
        return false;
    }
    if (sku && !item.variant?.sku?.toLowerCase().includes(sku.toLowerCase())) {
        return false;
    }
    if (barcode && !item.barcode?.toLowerCase().includes(barcode.toLowerCase())) {
        return false;
    }
    if (qtyExpected && !String(item.quantityExpected).includes(qtyExpected)) {
        return false;
    }
    if (qtyReceivedToDate && !String(item.quantityReceived).includes(qtyReceivedToDate)) {
        return false;
    }
    if (qtyReceived && !String(item.qtyReceived).includes(qtyReceived)) {
        return false;
    }

    // Item passes all filters
    return true;
};

/**
 * Apply filters to an array of items
 * Returns a new array containing only items that match all filters
 */
const applyFilters = (items, filters) => {
    const filteredItems = [];

    // Check each item against filters
    for (let i = 0; i < items.length; i++) {
        if (itemMatchesFilters(items[i], filters)) {
            filteredItems.push(items[i]);
        }
    }

    return filteredItems;
};

/**
 * Get the sortable value for an item based on the sort column
 */
const getSortValue = (item, column) => {
    switch (column) {
        case 'product':
            return item.variant?.product?.title;
        case 'vendorStyle':
            return item.variant?.title;
        case 'sku':
            return item.variant?.sku;
        case 'barcode':
            return item.barcode;
        case 'quantityExpected':
            return Number(item.quantityExpected);
        case 'quantityReceivedToDate':
            return Number(item.quantityReceived);
        case 'qtyReceived':
            return Number(item.qtyReceived);
        default:
            return '';
    }
};

/**
 * Sort an array of items by the specified column and direction
 */
const applySorting = (items, sortColumn, sortDirection) => {
    // If no sort column specified, return items as-is
    if (!sortColumn) return items;

    // Create a copy of the array to avoid mutating original
    const sortedItems = [...items];

    // Sort the array using comparison function
    sortedItems.sort((a, b) => {
        const aValue = getSortValue(a, sortColumn);
        const bValue = getSortValue(b, sortColumn);

        // Compare values and apply sort direction
        if (aValue < bValue) {
            return sortDirection === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortDirection === 'ascending' ? 1 : -1;
        }
        // Values are equal
        return 0;
    });

    return sortedItems;
};

/**
 * Hook for managing line items table state
 * Handles filtering, sorting, and editing functionality
 */
export const useLineItemsTable = (scannedVariants, setScannedVariants, setHasInteracted, setItemEditing, setUnsavedEdits, resetTriedToNavigateForRow) => {
    // Consolidated filter state object
    const [filters, setFilters] = useState({
        query: '', // General search across multiple fields
        product: '', // Filter by product title
        vendorStyle: '', // Filter by vendor style
        sku: '', // Filter by SKU
        barcode: '', // Filter by barcode
        qtyExpected: '', // Filter by expected quantity
        qtyReceivedToDate: '', // Filter by total received quantity
        qtyReceived: '', // Filter by quantity received in this session
    });

    // Sort state
    const [sortIndex, setSortIndex] = useState(null);
    const [sortDirection, setSortDirection] = useState('ascending');

    // Map sort index to column names
    const sortKeys = ['product', 'vendorStyle', 'sku', 'barcode', 'quantityExpected', 'quantityReceivedToDate', 'qtyReceived', null];
    const sortColumn = sortKeys[sortIndex];

    /**
     * Calculate filtered and sorted variants
     * Uses memoization to avoid expensive recalculations
     */
    const filteredAndSortedVariants = useMemo(() => {
        // First apply filters
        const filtered = applyFilters(scannedVariants, filters);
        // Then apply sorting
        return applySorting(filtered, sortColumn, sortDirection);
    }, [scannedVariants, filters, sortColumn, sortDirection]);

    const [editingKey, setEditingKey] = useState(null);
    const [editingQtyByKey, setEditingQtyByKey] = useState({});
    const [originalEditingQtyByKey, setOriginalEditingQtyByKey] = useState({});

    const getItemKey = (entry) => {
        const sku = entry?.variant?.sku || entry?.sku || '';
        const barcode = entry?.barcode || entry?.variant?.barcode || '';
        if (sku && barcode) return `${sku}-${barcode}`;
        if (sku) return `sku-${sku}`;
        if (barcode) return `barcode-${barcode}`;
        return undefined;
    };

    const derivedEditingIndex = useMemo(() => {
        if (!editingKey) return null;
        return filteredAndSortedVariants.findIndex((item) => getItemKey(item) === editingKey);
    }, [filteredAndSortedVariants, editingKey]);

    const derivedEditingQty = useMemo(() => {
        const map = {};
        for (let i = 0; i < filteredAndSortedVariants.length; i++) {
            const key = getItemKey(filteredAndSortedVariants[i]);
            if (key && editingQtyByKey[key] !== undefined) map[i] = editingQtyByKey[key];
        }
        return map;
    }, [filteredAndSortedVariants, editingQtyByKey]);

    /**
     * Update a specific filter value
     */
    const updateFilter = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    /**
     * Helper function to create a consistent item ID from entry data
     */
    const createItemId = (entry) => {
        const sku = entry.variant?.sku || entry.sku || '';
        const barcode = entry.barcode || entry.variant?.barcode || '';

        // Use both if available, fallback to whichever exists
        if (sku && barcode) {
            return `${sku}-${barcode}`;
        } else if (sku) {
            return `sku-${sku}`;
        } else if (barcode) {
            return `barcode-${barcode}`;
        }
    };

    /**
     * Start editing a quantity cell
     */
    const handleEditClick = useCallback(
        (index, value) => {
            const entry = filteredAndSortedVariants[index];
            if (!entry) return;

            const key = createItemId(entry);

            if (setItemEditing) {
                setItemEditing(key, true);
            }

            setOriginalEditingQtyByKey((prev) => ({ ...prev, [key]: value }));
            setEditingKey(key);
            setEditingQtyByKey((prev) => ({ ...prev, [key]: value }));
        },
        [setItemEditing, filteredAndSortedVariants]
    );

    /**
     * Update the editing quantity value
     */
    const handleQtyChange = useCallback(
        (value, index) => {
            const entry = filteredAndSortedVariants[index];

            if (!entry) return;

            const key = createItemId(entry);

            setEditingQtyByKey((prev) => ({ ...prev, [key]: value }));

            const originalValue = originalEditingQtyByKey[key];
            const hasActualChange = originalValue !== undefined && Number(value) !== Number(originalValue);

            if (hasActualChange && setUnsavedEdits) {
                setUnsavedEdits(true);
            } else if (!hasActualChange && setUnsavedEdits) {
                setUnsavedEdits(false);
            }
        },
        [filteredAndSortedVariants, originalEditingQtyByKey, setUnsavedEdits]
    );

    /**
     * Save the manually updated quantity
     */
    const handleManualUpdateOfTotal = useCallback(
        (filteredIndex) => {
            const entry = filteredAndSortedVariants[filteredIndex];

            if (!entry) return;

            const key = createItemId(entry);

            const inputQty = Number(editingQtyByKey[key] ?? derivedEditingQty[filteredIndex]);
            const prevQty = Number(entry.qtyReceived);

            // Calculate the difference to adjust total quantity
            const qtyDiff = inputQty - prevQty;
            const updatedQuantityReceived = entry.quantityReceived + qtyDiff;

            // Clear editing state first
            if (setItemEditing) {
                setItemEditing(key, false);
            }

            // Update the scanned variants array
            const updatedVariants = [];
            for (let i = 0; i < scannedVariants.length; i++) {
                const item = scannedVariants[i];
                if (item.variant?.sku === entry.variant?.sku && item.barcode === entry.barcode) {
                    updatedVariants.push({
                        ...item,
                        qtyReceived: inputQty,
                        quantityReceived: updatedQuantityReceived,
                    });
                } else {
                    updatedVariants.push(item);
                }
            }

            setScannedVariants(updatedVariants);

            // Only set hasInteracted and unsaved edits flags if the value actually changed from the original
            const originalValue = originalEditingQtyByKey[key];
            const hasActualChange = originalValue !== undefined && Number(inputQty) !== Number(originalValue);

            if (hasActualChange) {
                setHasInteracted(true);
                if (setUnsavedEdits) setUnsavedEdits(true);
            }

            if (setUnsavedEdits) setUnsavedEdits(false);

            if (resetTriedToNavigateForRow) resetTriedToNavigateForRow(key);

            setEditingKey(null);
            setEditingQtyByKey((prev) => {
                const copy = { ...prev };
                delete copy[key];
                return copy;
            });
            setOriginalEditingQtyByKey((prev) => {
                const copy = { ...prev };
                delete copy[key];
                return copy;
            });
        },
        [filteredAndSortedVariants, editingQtyByKey, derivedEditingQty, scannedVariants, setScannedVariants, setItemEditing, setHasInteracted, setUnsavedEdits, resetTriedToNavigateForRow]
    );

    /**
     * Cancel the current editing session
     */
    const handleCancelEdit = useCallback(() => {
        if (editingKey && setItemEditing) {
            setItemEditing(editingKey, false);
        }

        // Clear key-based tracking
        if (editingKey) {
            setOriginalEditingQtyByKey((prev) => {
                const copy = { ...prev };
                delete copy[editingKey];
                return copy;
            });
            setEditingQtyByKey((prev) => {
                const copy = { ...prev };
                delete copy[editingKey];
                return copy;
            });
        }

        if (setUnsavedEdits) setUnsavedEdits(false);

        // Clear the navigation flag for this row since they canceled
        if (resetTriedToNavigateForRow && editingKey) {
            resetTriedToNavigateForRow(editingKey);
        }

        setEditingKey(null);
    }, [editingKey, setItemEditing, resetTriedToNavigateForRow, setUnsavedEdits]);

    return {
        // Filter getters and setters
        queryValue: filters.query,
        setQueryValue: (value) => updateFilter('query', value),
        productFilter: filters.product,
        setProductFilter: (value) => updateFilter('product', value),
        vendorStyleFilter: filters.vendorStyle,
        setVendorStyleFilter: (value) => updateFilter('vendorStyle', value),
        skuFilter: filters.sku,
        setSkuFilter: (value) => updateFilter('sku', value),
        barcodeFilter: filters.barcode,
        setBarcodeFilter: (value) => updateFilter('barcode', value),
        qtyExpectedFilter: filters.qtyExpected,
        setQtyExpectedFilter: (value) => updateFilter('qtyExpected', value),
        qtyReceivedToDateFilter: filters.qtyReceivedToDate,
        setQtyReceivedToDateFilter: (value) => updateFilter('qtyReceivedToDate', value),
        qtyReceivedFilter: filters.qtyReceived,
        setQtyReceivedFilter: (value) => updateFilter('qtyReceived', value),

        // Sort state
        sortIndex,
        setSortIndex,
        sortDirection,
        setSortDirection,

        // Edit state (derived from stable key-based tracking)
        editingIndex: derivedEditingIndex,
        editingQty: derivedEditingQty,

        // Event handlers
        handleEditClick,
        handleQtyChange,
        handleCancelEdit,
        handleManualUpdateOfTotal,

        // Computed data
        filteredAndSortedVariants,
    };
};

/**
 * Hook for managing manual purchase order entry
 * Handles manual SKU/barcode entry and finishing scan operations
 */
export const usePurchaseOrderEntry = (props) => {
    const {
        entryMode,
        poId,
        qtyValue,
        rdId,
        receivingLineItemsActive,
        receivingCartonsActive,
        setLastScanned,
        setQtyValue,
        setScannedVariants,
        setFinishedScanning,
        handleReceivingLineItems,
        handleReceivingCarton,
        purchaseOrder,
        setLastAdded,
        setHasInteracted,
    } = props;

    // Loading states for different operations
    const [manualEntryLoading, setManualEntryLoading] = useState(false);
    const [submitManualEntryLoading, setSubmitManualEntryLoading] = useState(false);
    const [submitFinishScanningLoading, setSubmitFinishScanningLoading] = useState(false);

    // Input values for manual entry
    const [skuValue, setSkuValue] = useState('');
    const [barcodeValue, setBarcodeValue] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [focusTarget, setFocusTarget] = useState(null);

    const skuInputRef = useRef(null);
    const lpnInputRef = useRef(null);

    useEffect(() => {
        if (focusTarget === 'sku' && skuInputRef.current) {
            const skuInputField = skuInputRef.current.querySelector('input');

            if (skuInputField) {
                skuInputField.focus();
            }

            setFocusTarget(null);
        } else if (focusTarget === 'lpn' && lpnInputRef.current) {
            const lpnInputField = lpnInputRef.current.querySelector('input');

            if (lpnInputField) {
                lpnInputField.focus();
            }

            setFocusTarget(null);
        }
    }, [focusTarget]);

    // This is in charge of handling the field errors when moving between forms (line items & cartons)
    useEffect(() => {
        setFieldErrors({});
    }, [receivingLineItemsActive, receivingCartonsActive]);

    const { addToast } = useToast();

    /**
     * Handle finishing the scanning process
     * Calls appropriate handlers based on receiving mode
     */
    const handleFinishScanning = useCallback(async () => {
        setSubmitFinishScanningLoading(true);

        try {
            // Handle line items if that mode is active
            if (receivingLineItemsActive && handleReceivingLineItems) {
                await handleReceivingLineItems();
            }

            // Handle cartons if that mode is active
            if (receivingCartonsActive && handleReceivingCarton) {
                await handleReceivingCarton();
            }

            // Reset scanning flag to allow more scanning
            setFinishedScanning(false);
        } finally {
            setSubmitFinishScanningLoading(false);
        }
    }, [receivingLineItemsActive, receivingCartonsActive, handleReceivingLineItems, handleReceivingCarton, setFinishedScanning]);

    /**
     * Process carton submission for manual entry
     */
    const processCartonSubmission = useCallback(async () => {
        // Fetch carton data from server
        const data = await fetchCartonLinesByLicensePlate(poId, rdId, barcodeValue);

        let productOnPurchaseOrder = false;
        // Transform carton lines into variant objects
        const newVariants = [];
        for (let i = 0; i < data.cartonLines.length; i++) {
            const line = data.cartonLines[i];
            productOnPurchaseOrder = true;
            newVariants.push({
                ...line,
                variant: line.variant,
                barcode: line.variant.barcode,
                qtyReceived: line.quantityExpected,
                quantityExpected: getQuantityExpectedForSku(line?.sku, purchaseOrder),
                quantityReceived: getTotalQuantityReceivedForSkuAcrossDocs(line?.sku, purchaseOrder) + line.quantityExpected,
                id: line.id,
                productOnPurchaseOrder: productOnPurchaseOrder,
            });
        }

        const flashItems = [];

        const collectForFlash = (item) => {
            flashItems.push({ barcode: item.barcode, sku: item.variant?.sku });
        };

        // Add new variants to existing ones using helper function
        setScannedVariants((prev) => {
            return updateOrAddItems({
                prevArray: prev,
                newItems: newVariants,
                getKey: (item) => `${item.barcode}-${item.variant?.sku || ''}`,
                updateItem: (existing, newItem) => ({
                    ...existing,
                    qtyReceived: Number(existing.qtyReceived) + Number(newItem.qtyReceived),
                    quantityReceived: Number(existing.quantityReceived) + Number(newItem.qtyReceived),
                }),
                onMatch: collectForFlash,
                onAdd: collectForFlash,
            });
        });

        // Flash items sequentially
        if (flashItems.length > 0) {
            flashItems.forEach((item, index) => {
                setTimeout(() => {
                    setLastAdded(item);
                    // Clear after the last item
                    if (index === flashItems.length - 1) {
                        setTimeout(() => setLastAdded(null), 500);
                    }
                }, index * 100); // 100ms delay between each flash
            });
        }

        // Show success message
        addToast({
            type: 'success',
            message: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.CARTON} ${barcodeValue} ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.LOADED_SUCCESSFULLY}`,
        });
    }, [poId, rdId, barcodeValue, purchaseOrder, setScannedVariants, setLastAdded]);

    /**
     * Process line item submission for manual entry
     */
    const processLineItemSubmission = useCallback(async () => {
        // Fetch product data from server
        const {
            variant: product,
            barcode: codeUsed,
            quantityExpected,
        } = await fetchProductFromServer({
            barcode: barcodeValue,
            sku: skuValue,
            poId,
            rdId,
            purchaseOrder,
        });

        // Exit if no product found
        if (!product) return;

        // Calculate quantities and product status
        const qtyToAdd = Number(qtyValue);
        const persistedTotal = getTotalQuantityReceivedForSkuAcrossDocs(product?.sku, purchaseOrder);

        // Check if product is on the purchase order
        let productOnPurchaseOrder = false;
        if (purchaseOrder?.purchaseOrderLines) {
            for (let i = 0; i < purchaseOrder.purchaseOrderLines.length; i++) {
                if (purchaseOrder.purchaseOrderLines[i].sku === product?.sku) {
                    productOnPurchaseOrder = true;
                    break;
                }
            }
        }

        // Update scanned variants using helper function
        const flashAndReturn = (item) => {
            setLastAdded({ barcode: item.barcode, sku: item.variant?.sku });
            setTimeout(() => setLastAdded(null), 500);
        };

        setScannedVariants((prev) => {
            return updateOrAddSingleItem({
                prevArray: prev,
                newItem: { product, codeUsed, qtyToAdd, quantityExpected, persistedTotal, productOnPurchaseOrder },
                matcher: (item) => (!!skuValue && item.variant?.sku === skuValue) || (!!barcodeValue && item.barcode === barcodeValue),
                updateItem: (existing) => ({
                    ...existing,
                    qtyReceived: Number(existing.qtyReceived) + qtyToAdd,
                    quantityReceived: Number(existing.quantityReceived) + qtyToAdd,
                    quantityExpected: quantityExpected,
                    productOnPurchaseOrder,
                    entryMethod: PURCHASE_ORDER_STRINGS.MANUAL_ENTRY,
                }),
                createItem: () => ({
                    variant: product,
                    barcode: codeUsed,
                    qtyReceived: qtyToAdd,
                    quantityExpected: quantityExpected,
                    quantityReceived: persistedTotal + qtyToAdd,
                    productOnPurchaseOrder,
                    entryMethod: PURCHASE_ORDER_STRINGS.MANUAL_ENTRY,
                }),
                onMatch: flashAndReturn,
                onAdd: flashAndReturn,
            });
        });

        // Update last scanned info and show success message
        setLastScanned({ variant: product, barcode: codeUsed || barcodeValue || skuValue });
        const toast = getToastForEntryMode(entryMode, codeUsed || barcodeValue || skuValue);
        addToast(toast);
    }, [barcodeValue, skuValue, poId, rdId, qtyValue, purchaseOrder, setScannedVariants, setLastScanned, setLastAdded]);

    const manualEntrySchema = z
        .object({
            sku: z.string().optional(),
            barcode: z.string().optional(),
            quantity: z
                .string()
                .refine((quantity) => quantity > 0, {
                    message: PURCHASE_ORDER_STRINGS.ERRORS.QUANTITY_MUST_BE_POSITIVE,
                })
                .optional(),
        })
        .superRefine((data, ctx) => {
            if (!data.sku && !data.barcode) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: PURCHASE_ORDER_STRINGS.ERRORS.SKU_REQUIRED,
                    path: ['sku'],
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: receivingCartonsActive === true ? PURCHASE_ORDER_STRINGS.ERRORS.LICENSE_PLATE_REQUIRED : PURCHASE_ORDER_STRINGS.ERRORS.BARCODE_REQUIRED,
                    path: ['barcode'],
                });
            }
        });
    /**
     * Handle manual submission of SKU/barcode entry
     */
    const handleManualSubmit = useCallback(async () => {
        // Only proceed if in manual entry mode
        if (entryMode !== 'manual-entry') return;

        const result = manualEntrySchema.safeParse({
            sku: skuValue,
            barcode: barcodeValue,
            quantity: qtyValue,
        });

        if (!result.success) {
            const formatted = result.error.format();

            setFieldErrors({
                sku: formatted.sku?._errors,
                barcode: formatted.barcode?._errors,
                quantity: formatted.quantity?._errors,
            });

            return;
        } else {
            setFieldErrors({});
        }

        // Validate that at least one input is provided
        if (!barcodeValue && !skuValue) {
            addToast({
                type: 'error',
                message: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.MISSING_SKU_OR_BARCODE_ERROR,
            });
            return;
        }

        // Start loading and mark user interaction
        setFieldErrors({});
        setManualEntryLoading(true);
        setHasInteracted(true);

        try {
            // Process based on receiving mode
            if (receivingCartonsActive) {
                await processCartonSubmission();
            } else if (receivingLineItemsActive) {
                await processLineItemSubmission();
            }

            // Clear inputs on success
            setBarcodeValue('');
            setSkuValue('');
            setQtyValue('1');

            if (receivingCartonsActive) {
                setFocusTarget('lpn');
            } else if (receivingLineItemsActive) {
                setFocusTarget('sku');
            }
        } catch (error) {
            // Set appropriate error message based on mode
            const errorMessage = receivingCartonsActive
                ? `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.CARTON_NOT_FOUND_FOR_BARCODE}: ${barcodeValue}`
                : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.MISSING_SKU_OR_BARCODE_ERROR;

            addToast({
                type: 'error',
                message: errorMessage,
            });
        } finally {
            // Reset loading states
            setManualEntryLoading(false);
            setSubmitManualEntryLoading(false);
        }
    }, [
        entryMode,
        barcodeValue,
        skuValue,
        receivingCartonsActive,
        receivingLineItemsActive,
        processCartonSubmission,
        processLineItemSubmission,
        setHasInteracted,
        setBarcodeValue,
        setSkuValue,
        setQtyValue,
    ]);

    return {
        manualEntryLoading,
        submitManualEntryLoading,
        submitFinishScanningLoading,
        skuValue,
        setSkuValue,
        barcodeValue,
        setBarcodeValue,
        handleFinishScanning,
        handleManualSubmit,
        fieldErrors,
        setFieldErrors,
        skuInputRef,
        lpnInputRef,
    };
};

/**
 * Hook for receiving document save/submit actions
 * Handles saving drafts and submitting completed documents
 */

export const useReceivingDocumentActions = ({ poId, rdId, asnId, scannedVariants, setStatus, setHasLocalStatusUpdate, onRdIdUpdate, onNameUpdate, entryMode }) => {
    // Loading states for document operations
    const [submitDocumentLoading, setSubmitDocumentLoading] = useState(false);
    const [submitSaveDraftLoading, setSaveDraftLoading] = useState(false);

    const { addToast } = useToast();

    // Base URL for API requests
    const baseUrl = `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}/receiving-document/${rdId}`;

    /**
     * Generic function to make document requests (save draft or submit)
     */
    const makeRequest = useCallback(
        async (action, status, setLoading) => {
            setLoading(true);

            try {
                // Make API request
                const response = await axios.post(
                    baseUrl,
                    {
                        poId,
                        rdId,
                        asnId,
                        status,
                        lineItems: scannedVariants,
                        action,
                        entryMethod: entryMode,
                    },
                    {
                        headers: { 'Content-Type': 'application/json' },
                    }
                );

                // Check if request was successful
                if (response?.data?.data?.receivingDocument) {
                    const responseData = response.data.data;

                    // Handle rdId transition from 'new' to real ID
                    if (responseData.wasNewDocument && responseData.rdId && onRdIdUpdate) {
                        onRdIdUpdate(responseData.rdId, responseData.wasNewDocument);
                    }

                    if (responseData.receivingDocument?.name && onNameUpdate) {
                        onNameUpdate(responseData.receivingDocument.name);
                    }

                    // Set appropriate success message based on action
                    const successMessage =
                        status === 'Draft'
                            ? PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.DRAFT_HAS_BEEN_SAVED
                            : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.DOCUMENT_HAS_BEEN_SUBMITTED;

                    addToast({
                        type: 'success',
                        message: successMessage,
                    });
                    setStatus(status);
                    setHasLocalStatusUpdate(true);
                } else {
                    throw new Error(`${action} failed: missing receiving document in response.`);
                }
            } catch (error) {
                // Set appropriate error message based on action
                const errorMessage =
                    status === 'Draft'
                        ? PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.DRAFT_HAS_NOT_BEEN_SAVED
                        : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.DOCUMENT_HAS_NOT_BEEN_SUBMITTED;

                addToast({
                    type: 'error',
                    message: error?.response?.data?.message || errorMessage,
                });
            } finally {
                setLoading(false);
            }
        },
        [baseUrl, poId, rdId, asnId, scannedVariants, setStatus, setHasLocalStatusUpdate, onRdIdUpdate, onNameUpdate, entryMode]
    );

    /**
     * Handle saving document as draft
     */
    const handleSaveDraft = useCallback(async () => {
        await makeRequest('save-draft', 'Draft', setSaveDraftLoading);
    }, [makeRequest]);

    /**
     * Handle submitting document
     */
    const handleSubmitDocument = useCallback(async () => {
        await makeRequest('submit', 'Completed', setSubmitDocumentLoading);
    }, [makeRequest]);

    return {
        handleSaveDraft,
        handleSubmitDocument,
        submitDocumentLoading,
        submitSaveDraftLoading,
    };
};
/**
 * Hook for handling barcode scanner input
 * Listens for keyboard events that simulate barcode scanner input
 */
export const useBarcodeScanner = ({
    enabled,
    poId,
    rdId,
    receivingCartonsActive,
    receivingLineItemsActive,
    entryMode,
    fetchCartonLinesByLicensePlate,
    fetchProductFromServer,
    setScannedVariants,
    setLoading,
    setValue,
    setLastScanned,
    purchaseOrder,
    setLastAdded,
    setHasInteracted,
}) => {
    // Refs for managing scanner input buffer and timing
    const scannedRef = useRef('');
    const timerRef = useRef(null);

    const { addToast } = useToast();

    /**
     * Process scanned carton barcode
     */
    const processCartonScan = useCallback(
        async (barcodeValue) => {
            // Fetch carton data from server
            const data = await fetchCartonLinesByLicensePlate(poId, rdId, barcodeValue);

            // Check if any carton lines were found
            if (!data.cartonLines[0]) {
                addToast({
                    type: 'error',
                    message: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.NO_LINE_ITEMS_FOUND_ERROR}: ${barcodeValue}`,
                });
                return;
            }

            let productOnPurchaseOrder = false;
            // Transform carton lines into enriched variants
            const enrichedVariants = [];
            for (let i = 0; i < data.cartonLines.length; i++) {
                productOnPurchaseOrder = true;
                const line = data.cartonLines[i];
                enrichedVariants.push({
                    ...line,
                    barcode: line.variant?.barcode,
                    qtyReceived: line.quantityExpected,
                    quantityExpected: getQuantityExpectedForSku(line?.sku, purchaseOrder),
                    quantityReceived: getTotalQuantityReceivedForSkuAcrossDocs(line.sku, purchaseOrder) + line.quantityExpected,
                    id: line.id,
                    productOnPurchaseOrder: productOnPurchaseOrder,
                });
            }

            // Collect all items that will be flashed
            const flashItems = [];

            const collectForFlash = (item) => {
                flashItems.push({ barcode: item.barcode, sku: item.variant?.sku });
            };

            // Add new variants to existing list using helper function
            setScannedVariants((prev) => {
                return updateOrAddItems({
                    prevArray: prev,
                    newItems: enrichedVariants,
                    getKey: (item) => `${item.barcode}-${item.variant?.sku}`,
                    updateItem: (existing, newItem) => ({
                        ...existing,
                        qtyReceived: Number(existing.qtyReceived) + Number(newItem.qtyReceived),
                        quantityReceived: Number(existing.quantityReceived) + Number(newItem.qtyReceived),
                    }),
                    onMatch: collectForFlash,
                    onAdd: collectForFlash,
                });
            });

            // Flash items sequentially
            if (flashItems.length > 0) {
                flashItems.forEach((item, index) => {
                    setTimeout(() => {
                        setLastAdded(item);
                        // Clear after the last item
                        if (index === flashItems.length - 1) {
                            setTimeout(() => setLastAdded(null), 500);
                        }
                    }, index * 100); // 100ms delay between each flash
                });
            }

            // Show success message
            addToast({
                type: 'success',
                message: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.CARTON} ${barcodeValue} ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ENTRY_SECTION.LOADED_SUCCESSFULLY}`,
            });
        },
        [poId, rdId, fetchCartonLinesByLicensePlate, purchaseOrder, setScannedVariants, setLastAdded]
    );

    /**
     * Process scanned line item barcode
     */
    const processLineItemScan = useCallback(
        async (barcodeValue) => {
            // Fetch product data from server
            const result = await fetchProductFromServer({ barcode: barcodeValue, sku: '', poId, rdId, purchaseOrder });
            const product = result?.variant;
            const codeUsed = result?.barcode || barcodeValue;

            // Exit if no product found
            if (!product) {
                addToast({
                    type: 'error',
                    message: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.NO_PRODUCT_DATA_RETURNED_ERROR} ${codeUsed}`,
                });
                return;
            }

            // Calculate expected quantity and total received
            const expectedQty = getQuantityExpectedForSku(product?.sku, purchaseOrder);
            const persistedTotal = getTotalQuantityReceivedForSkuAcrossDocs(product?.sku, purchaseOrder);

            // Check if product is on the purchase order
            let productOnPurchaseOrder = false;
            if (purchaseOrder?.purchaseOrderLines) {
                for (let i = 0; i < purchaseOrder.purchaseOrderLines.length; i++) {
                    if (purchaseOrder.purchaseOrderLines[i].sku === product?.sku) {
                        productOnPurchaseOrder = true;
                        break;
                    }
                }
            }

            // Update scanned variants using helper function
            const flashAndReturn = (item) => {
                setLastAdded({ barcode: item.barcode, sku: item.variant?.sku });
                setTimeout(() => setLastAdded(null), 500);
            };

            setScannedVariants((prev) => {
                return updateOrAddSingleItem({
                    prevArray: prev,
                    newItem: { codeUsed, product, expectedQty, persistedTotal, productOnPurchaseOrder },
                    matcher: (item) => item.barcode === codeUsed && item.variant?.sku === product.sku,
                    updateItem: (existing) => ({
                        ...existing,
                        qtyReceived: Number(existing.qtyReceived) + 1,
                        quantityExpected: expectedQty,
                        quantityReceived: Number(existing.quantityReceived) + 1,
                        productOnPurchaseOrder,
                        entryMethod: PURCHASE_ORDER_STRINGS.SCAN_MODE,
                    }),
                    createItem: () => ({
                        variant: product,
                        barcode: product.barcode || codeUsed,
                        sku: product.sku || '',
                        qtyReceived: 1,
                        quantityExpected: expectedQty,
                        quantityReceived: persistedTotal + 1,
                        productOnPurchaseOrder,
                        entryMethod: PURCHASE_ORDER_STRINGS.SCAN_MODE,
                    }),
                    onMatch: flashAndReturn,
                    onAdd: flashAndReturn,
                });
            });

            // Update last scanned info and show success message
            setLastScanned({ variant: product, barcode: codeUsed });
            const toast = getToastForEntryMode(entryMode, codeUsed || barcodeValue || product?.sku);
            addToast(toast);
        },
        [fetchProductFromServer, poId, rdId, purchaseOrder, setScannedVariants, setLastScanned, setLastAdded]
    );

    useEffect(() => {
        const handleKeyPress = async (event) => {
            // Collect characters from barcode scanner
            if (typeof event.key === 'string' && event.key.length === 1) {
                scannedRef.current += event.key;

                // Reset buffer timer on each keypress
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    scannedRef.current = '';
                }, 300);
            }

            // Process complete barcode when Enter is pressed
            if (event.key === 'Enter' && enabled !== false && entryMode === 'scan-mode') {
                const barcodeValue = scannedRef.current.trim();
                scannedRef.current = '';

                // Start loading and mark user interaction
                setLoading(true);
                setHasInteracted(true);
                setValue?.('');

                try {
                    // Process based on receiving mode
                    if (receivingCartonsActive) {
                        await processCartonScan(barcodeValue);
                    } else if (receivingLineItemsActive) {
                        await processLineItemScan(barcodeValue);
                    }
                } catch (error) {
                    // Set appropriate error message based on mode
                    const errorMessage = receivingCartonsActive
                        ? `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.CARTON_NOT_FOUND_ERROR}: ${barcodeValue}`
                        : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.MISSING_SKU_OR_BARCODE_ERROR;

                    addToast({
                        type: 'error',
                        message: errorMessage,
                    });
                } finally {
                    setLoading(false);
                }
            }
        };

        // Add event listener for barcode scanner
        window.addEventListener('keypress', handleKeyPress);

        // Cleanup function
        return () => {
            window.removeEventListener('keypress', handleKeyPress);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [enabled, entryMode, receivingCartonsActive, receivingLineItemsActive, processCartonScan, processLineItemScan, setLoading, setHasInteracted, setValue]);
};

/**
 * Simple loading state hook for receiving documents
 * Sets loading to true when no scanned variants exist and no error is present
 */
export const useReceivingDocumentLoading = ({ scannedVariants, error, setLoading }) => {
    useEffect(() => {
        // Show loading when there are no scanned variants and no error
        setLoading(scannedVariants.length === 0 && !error);
    }, [scannedVariants, error, setLoading]);
};
